import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'components/ui/toast';
import { useReturnFocus } from 'hooks/useReturnFocus';
import {
  TUTORIAL_ACCEPT,
  TUTORIAL_DESCRIPTION_MAX,
  TUTORIAL_TITLE_MAX,
  isTutorialContentType,
  tutorialFileError,
  tutorialsApi,
  uploadTutorialFile,
  uploadTutorialThumbnail,
} from 'api/tutorialsApi';
import { captureVideoFrame } from 'utils/captureVideoFrame';
import { megabytes, tutorialCategoryLabel } from './utils';

interface UploadTutorialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: string[];
  onUploaded: () => void;
}

const stripExtension = (fileName: string) => fileName.replace(/\.[^.]+$/, '');
const hasStatus = (error: Error, status: number) => 'statusCode' in error && error.statusCode === status;

const UploadTutorialDialog = ({ open, onOpenChange, categories, onUploaded }: UploadTutorialDialogProps) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const returnFocus = useReturnFocus();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [chosenCategory, setChosenCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const busy = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const category = chosenCategory ?? categories[0] ?? '';

  useEffect(() => {
    if (!uploading) return undefined;
    const block = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', block);
    return () => window.removeEventListener('beforeunload', block);
  }, [uploading]);

  useEffect(() => () => controller.current?.abort(), []);

  const reset = () => {
    setFile(null);
    setName('');
    setDescription('');
    setChosenCategory(null);
    setError(null);
    setProgress(0);
  };

  const handleOpenChange = (next: boolean) => {
    if (uploading) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0] ?? null;
    setFile(picked);
    setError(null);
    if (picked) setName(stripExtension(picked.name).slice(0, TUTORIAL_TITLE_MAX));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy.current) return;
    const title = name.trim();
    const fileError = file && tutorialFileError(file);
    if (!file) return setError(t('Choose a file'));
    if (fileError === 'type' || !isTutorialContentType(file.type)) return setError(t('Only MP4 and WebM videos are accepted'));
    if (fileError === 'empty') return setError(t('This file is empty'));
    if (fileError === 'size') return setError(t('This file is larger than 2 GiB'));
    if (!title) return setError(t('Enter a name'));

    busy.current = true;
    const abort = new AbortController();
    controller.current = abort;
    setError(null);
    setProgress(0);
    setUploading(true);
    try {
      const details = { title, description: description.trim() };
      const thumbnail = captureVideoFrame(file);
      const ticket = await tutorialsApi.requestUpload({ ...details, category, contentType: file.type, size: file.size });
      await uploadTutorialFile(ticket, file, details, setProgress, abort.signal);
      // The video is stored: a missing thumbnail only falls back to the video's own frame
      const frame = await thumbnail;
      if (frame) await uploadTutorialThumbnail(ticket, frame).catch(() => undefined);
      reset();
      onUploaded();
      toast.success(t('Tutorial added'));
      setUploading(false);
      onOpenChange(false);
    } catch (failure) {
      if (!abort.signal.aborted) {
        const tooLong = failure instanceof Error && hasStatus(failure, 403);
        setError(tooLong ? t('Upload took too long, try again') : t('Upload failed, try again'));
      }
      setUploading(false);
    } finally {
      busy.current = false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[480px]" onEscapeKeyDown={(e) => uploading && e.preventDefault()} {...returnFocus}>
        <DialogHeader>
          <DialogTitle>{t('Add a tutorial')}</DialogTitle>
          <DialogDescription>{t('Choose a video (MP4 or WebM, 2 GiB max)')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tutorial-file">{t('File')}</Label>
            <Input id="tutorial-file" type="file" accept={TUTORIAL_ACCEPT} disabled={uploading} onChange={handleFile} />
            {file && (
              <p className="text-xs text-muted-foreground">
                {file.name} · {megabytes(file.size, i18n.language)}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tutorial-name">{t('Name')}</Label>
            <Input
              id="tutorial-name"
              value={name}
              maxLength={TUTORIAL_TITLE_MAX}
              disabled={uploading}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tutorial-description">{t('Description (optional)')}</Label>
            <Textarea
              id="tutorial-description"
              value={description}
              rows={3}
              maxLength={TUTORIAL_DESCRIPTION_MAX}
              disabled={uploading}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tutorial-category">{t('Category')}</Label>
            <Select value={category} onValueChange={setChosenCategory} disabled={uploading}>
              <SelectTrigger id="tutorial-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((key) => (
                  <SelectItem key={key} value={key}>
                    {tutorialCategoryLabel(t, key)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {uploading && (
            <div className="flex flex-col gap-1.5">
              <progress className="h-2 w-full" value={progress} max={1} aria-labelledby="tutorial-progress" />
              <p id="tutorial-progress" className="text-xs text-muted-foreground">
                {t('Uploading… {{percent}}%', { percent: Math.round(progress * 100) })}
              </p>
            </div>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => (uploading ? controller.current?.abort() : handleOpenChange(false))}>
              {uploading ? t('Cancel upload') : t('Cancel')}
            </Button>
            <Button type="submit" disabled={uploading}>
              {t('Send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadTutorialDialog;
